import React from 'react'
import DisplayField from '../../../../components/Form/DisplayField'
import Badge from '../../../../components/Badge'
import Panel, { PanelProps } from '../../../../components/Panel'
import { formatUTCDate, formatCurrency } from '../../../../util/string'
import { ServiceAccount } from '../../../../apis/types'

export interface CurrentInvoiceSummaryProps extends Omit<PanelProps, 'header'> {
  serviceAccount: ServiceAccount
  /** Render fields without Panel chrome (e.g. inside a modal). */
  embedded?: boolean
}

const InvoiceFields: React.FC<{ serviceAccount: ServiceAccount }> = ({ serviceAccount }) => {
  const invoice = serviceAccount.currentInvoice
  if (invoice == null) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">No current invoice</p>
  }

  const { number, dueDate, amount, remainingBalance, status, billingStartDate, billingEndDate } = invoice

  return (
    <div className="grid grid-cols-1 gap-2">
      <DisplayField label="Invoice number">{number}</DisplayField>
      <DisplayField label="Statement period">{`${formatUTCDate(new Date(billingStartDate))} - ${formatUTCDate(new Date(billingEndDate))}`}</DisplayField>
      <DisplayField label="Due date">{formatUTCDate(new Date(dueDate))}</DisplayField>
      <DisplayField label="Original balance">{formatCurrency(amount)}</DisplayField>
      <DisplayField label="Amount due">{formatCurrency(remainingBalance)}</DisplayField>
      <DisplayField label="Status">
        <Badge status={status} />
      </DisplayField>
    </div>
  )
}

export const CurrentInvoiceSummary: React.FC<CurrentInvoiceSummaryProps> = (props) => {
  const { serviceAccount, embedded = false, ...rest } = props

  if (serviceAccount.currentInvoice == null) {
    return null
  }

  if (embedded) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Current invoice
        </div>
        <InvoiceFields serviceAccount={serviceAccount} />
      </div>
    )
  }

  return (
    <Panel header="Current invoice" {...rest}>
      <InvoiceFields serviceAccount={serviceAccount} />
    </Panel>
  )
}

export default CurrentInvoiceSummary
